import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ApproveRejectActions } from 'modules/shared/components';
import { InlineApproveSection, InlineRejectSection, ScoredInterviewRound } from '../components';
import { INTERVIEW_ACTION } from '../interview.constants';

const ROUND_1_CRITERIA = Object.freeze([
  { key: 'integratedReasoning', label: 'Integrated Reasoning', max: 20 },
  { key: 'quantitativeReasoning', label: 'Quantitative Reasoning', max: 20 },
  { key: 'verbalReasoning', label: 'Verbal Reasoning', max: 20 },
]);

const CURRENT_ROUND_KEY = 'round1';

/**
 * InterviewRound1
 *
 * Controls evaluation and decision flow for the first interview round.
 *
 * Responsibilities:
 * - Render scored evaluation criteria for Round 1
 * - Calculate and display total score
 * - Gate approve / reject actions behind validation
 * - Emit structured decision payloads to parent handlers
 *
 * Props:
 * @param {Object} data - Existing round data (scores) when revisiting the round.
 * @param {boolean} readOnly - Disables actions and editing when true.
 * @param {Function} onApprove - Callback invoked with approval payload.
 * @param {Function} onReject - Callback invoked with rejection payload.
 *
 * Notes:
 * - Uses react-hook-form internally but does not expose form state upward.
 * - Action mode is local and resets on cancel.
 */
export default function InterviewRound1({ data, readOnly, onApprove, onReject }) {
  const [actionMode, setActionMode] = useState<string>(INTERVIEW_ACTION.IDLE);
  // null | 'approve' | 'reject'

  const methods = useForm({
    defaultValues: {
      round1: data ?? {},
      rejectionReason: '',
      nextInterview: null,
    },
  });
  const { watch, trigger, resetField } = methods;

  const scores = watch('round1');
  const rejectionReason = watch('rejectionReason');
  const nextInterview = watch('nextInterview');

  const totalScore = ROUND_1_CRITERIA.reduce((acc, item) => {
    const value = Number(scores?.[item.key]?.score);
    return acc + (Number.isFinite(value) ? value : 0);
  }, 0);

  const resetActionState = () => {
    setActionMode(INTERVIEW_ACTION.IDLE);
    resetField('rejectionReason');
  };

  const handleApprove = async () => {
    const isFormValid = await trigger(['round1', 'nextInterview'], { shouldFocus: true });
    if (!isFormValid) return;
    onApprove?.({
      action: 'approve',
      round1: scores,
      nextInterview,
      currentRoundKey: CURRENT_ROUND_KEY,
    });
  };

  const handleReject = async () => {
    const isFormValid = await trigger(['rejectionReason', 'round1'], { shouldFocus: true });
    if (!isFormValid) return;
    onReject?.({ round1: scores, rejectionReason, action: INTERVIEW_ACTION.REJECT });
  };

  return (
    <FormProvider {...methods}>
      <form className='column gap-4'>
        <ScoredInterviewRound
          title='ROUND 1 - APTITUDE'
          roundKey='round1'
          criteria={ROUND_1_CRITERIA}
          readOnly={readOnly}
          totalScore={totalScore}
        />
        {!readOnly && actionMode === INTERVIEW_ACTION.IDLE && (
          <ApproveRejectActions
            onApprove={async () => {
              const isValid = await trigger();
              if (!isValid) return;
              setActionMode(INTERVIEW_ACTION.APPROVE);
            }}
            onReject={async () => {
              const isValid = await trigger();
              if (!isValid) return;
              setActionMode(INTERVIEW_ACTION.REJECT);
            }}
          />
        )}
        {actionMode === INTERVIEW_ACTION.REJECT && (
          <InlineRejectSection
            name='rejectionReason'
            onCancel={resetActionState}
            onConfirm={handleReject}
            disabled={readOnly}
          />
        )}
        {actionMode === INTERVIEW_ACTION.APPROVE && (
          <InlineApproveSection
            name='nextInterview'
            onCancel={resetActionState}
            onConfirm={handleApprove}
            disabled={readOnly}
          />
        )}
      </form>
    </FormProvider>
  );
}
